import { ref, computed, watch } from 'vue';
import { taskMeta } from './taskMeta';

/**
 * HeroResume (v3 homepage) — core logic, shared by both portals.
 *
 * Prototype V3Hero, flow-agnostic: resumes or STARTS whatever the next task is.
 * - Resume: the user's most-recent in-progress evaluation from
 *   `POST Report/homeResume` (step N-of-M progress, Continue CTA).
 * - Start next (prototype "Start here"): when nothing is in progress, the next
 *   READY task from the injected task feed, with guided copy from the shared
 *   task-type meta ("You need to evaluate this role - guided, about 15
 *   minutes."). When neither exists the Hero falls back to its configured
 *   static content.
 *
 * `self` pattern; caller injects an axios-like `http` (+ optional task
 * getters). Navigation stays in the presentation layer.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => boolean} [options.getEnabled] resume mode on/off (default on)
 * @param {() => any[]}   [options.getTasks] task feed for the start-next path
 * @param {() => void} [options.onReady] called once after the first load settles
 */
// flow_type → icon + CTA verb + type label, so the Hero copy/icon reflect the
// task (never assumes "evaluation") — the shared taskMeta vocabulary.
const FLOW_LABEL = {
  EVALUATION: 'Job evaluation',
  CLASSIFICATION: 'Classification',
  RECLASSIFICATION: 'Re-classification',
  'RE-CLASSIFICATION': 'Re-classification',
  CALIBRATION: 'Calibration',
};
const flowMeta = (ft) => {
  const label = FLOW_LABEL[String(ft || '').toUpperCase()] || '';
  const m = taskMeta(label);
  return { icon: label ? m.icon : 'play_circle', verb: label ? m.verb : 'task', label };
};

// "Ready to start" heuristic over the lifecycle status name — same regexes as
// taskList/priorityStack (a per-tenant status→state map would make it exact).
const isReadyTask = (t) => {
  const s = String((t && (t.lifecycle_status_name || t.doc_status_text)) || '');
  return !/return|sent back|revis|reject|bounce|progress|draft|started|working|wait|await|pending|review|approv|with |complete|closed|done/i.test(s);
};
const dueSoon = (t) => {
  if (!t || !t.due_date) return false;
  const d = new Date(t.due_date);
  return !isNaN(d.getTime()) && d.getTime() - Date.now() <= 7 * 86400000;
};

export function heroResume(options) {
  const { http, getEnabled = () => true, getTasks = () => [], onReady = () => {} } = options || {};

  const self = { item: ref(null), loading: ref(false) };

  self.hasResume = computed(() => !!self.item.value);
  // Raw flow_type (STANDARD | GUIDED | CLASSIFICATION) — lets the presentation
  // route the Continue CTA per flow (e.g. classification → the levelling page).
  self.flowType = computed(() => (self.item.value && self.item.value.flow_type) || '');
  // Task-type-aware icon / CTA verb / type label for the resume item.
  self.flow = computed(() => flowMeta(self.item.value && self.item.value.flow_type));
  self.icon = computed(() => self.flow.value.icon);
  self.continueLabel = computed(() => 'Continue ' + self.flow.value.verb);
  self.typeLabel = computed(() => self.flow.value.label);
  self.totalSteps = computed(() => Number(self.item.value && self.item.value.total_steps) || 0);
  self.completedSteps = computed(() => Number(self.item.value && self.item.value.completed_steps) || 0);
  self.progressPct = computed(() =>
    self.totalSteps.value ? Math.min(100, Math.round((self.completedSteps.value / self.totalSteps.value) * 100)) : 0,
  );
  self.stepText = computed(() => (self.totalSteps.value ? `Step ${self.completedSteps.value} of ${self.totalSteps.value}` : ''));
  self.title = computed(() => {
    const it = self.item.value;
    return it ? it.doc_title || it.doc_name || it.reference || '' : '';
  });
  self.reference = computed(() => {
    const it = self.item.value;
    return it ? (it.system_number ?? it.reference ?? '') : '';
  });
  self.lastSavedText = computed(() => relTime(self.item.value && self.item.value.last_saved));

  // --- "Start here" (prototype V3Hero non-resume branch) ---
  // The next READY task from the injected feed, due-soon first.
  self.nextTask = computed(() => {
    if (self.hasResume.value || !getEnabled()) return null;
    const ready = (getTasks() || []).filter(isReadyTask);
    if (!ready.length) return null;
    return ready.slice().sort((a, b) => (dueSoon(b) ? 1 : 0) - (dueSoon(a) ? 1 : 0))[0];
  });
  self.hasNext = computed(() => !!self.nextTask.value);
  const nextMeta = computed(() => taskMeta(self.nextTask.value && (self.nextTask.value.task_type || self.nextTask.value.doc_type)));
  self.nextIcon = computed(() => nextMeta.value.icon);
  self.nextTitle = computed(() => {
    const t = self.nextTask.value;
    return t ? t.doc_title || t.doc_name || t.reference || '' : '';
  });
  self.nextReference = computed(() => {
    const t = self.nextTask.value;
    return t ? (t.system_number ?? t.reference ?? '') : '';
  });
  // "{type} · {ref} · {due}" (prototype hero meta line).
  self.nextMetaLine = computed(() => {
    const t = self.nextTask.value;
    if (!t) return '';
    let due = '';
    if (t.due_date) {
      const d = new Date(t.due_date);
      if (!isNaN(d.getTime())) due = 'Due ' + d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
    return [t.task_type || t.doc_type, t.reference, due].filter(Boolean).join(' · ');
  });
  // "You need to evaluate this role - guided, about 15 minutes." (the task feed
  // carries no inviter name, so the prototype's "{invitedBy} asked you to"
  // variant isn't reproducible — always the "You need to" phrasing.)
  self.nextGuidedLine = computed(() => {
    const m = nextMeta.value;
    const ask = m.ask.charAt(0).toLowerCase() + m.ask.slice(1);
    return `You need to ${ask} — guided, about ${m.mins} minutes.`;
  });
  self.startLabel = computed(() => 'Start ' + nextMeta.value.verb);
  // Eyebrow (prototype): resume → "Continue where you left off", next → "Start here".
  self.eyebrow = computed(() => (self.hasResume.value ? 'Continue where you left off' : self.hasNext.value ? 'Start here' : ''));

  let didReady = false;
  function signalReady() {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  }

  async function load() {
    if (!getEnabled()) {
      self.item.value = null;
      signalReady();
      return;
    }
    self.loading.value = true;
    try {
      const r = await http.post('Report/homeResume', {});
      self.item.value = (r && r.data && (r.data.Data ?? null)) || null;
    } catch {
      self.item.value = null;
    } finally {
      self.loading.value = false;
      signalReady();
    }
  }

  watch(() => getEnabled(), load, { immediate: true });

  return self;
}

/** Compact relative time for "last saved" (e.g. "2h ago", "3d ago", "5 Jun"). */
function relTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
